import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  collection, query, where, orderBy, limit, limitToLast, onSnapshot, doc, updateDoc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { closeSupportChat, sendSupportMessage } from '../utils/supportChat';

const MAX_TEXT = 1000;

function formatTime(ts) {
  if (!ts?.toDate) return '';
  return ts.toDate().toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function StatusBadge({ chat }) {
  if (!chat) return null;
  if (chat.status === 'pending_trip') {
    return <span className="badge badge-warning">Pendiente · en viaje</span>;
  }
  if (chat.status === 'open') {
    return chat.appAckAt
      ? <span className="badge badge-success">Abierto · ✓ visto en la app</span>
      : <span className="badge badge-danger" title="La app puede estar desactualizada, sin conexión o cerrada">Abierto · sin confirmar</span>;
  }
  return <span className="badge badge-info">Cerrado</span>;
}

function SupportChats() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedUid = searchParams.get('uid');

  const [tab, setTab] = useState('active'); // 'active' | 'closed'
  const [chats, setChats] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const bottomRef = useRef(null);

  // Lista de chats
  useEffect(() => {
    setLoadingList(true);
    const q = tab === 'active'
      ? query(collection(db, 'support_chats'), where('status', 'in', ['open', 'pending_trip']), orderBy('lastMessageAt', 'desc'))
      : query(collection(db, 'support_chats'), where('status', '==', 'closed'), orderBy('lastMessageAt', 'desc'), limit(50));

    return onSnapshot(q, (snap) => {
      setChats(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoadingList(false);
    }, (error) => {
      console.error('Error cargando chats de soporte:', error);
      setLoadingList(false);
    });
  }, [tab]);

  // Chat seleccionado (se escucha aparte: puede no estar en la pestaña actual)
  useEffect(() => {
    if (!selectedUid) { setSelectedChat(null); return; }
    return onSnapshot(doc(db, 'support_chats', selectedUid), (snap) => {
      setSelectedChat(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    });
  }, [selectedUid]);

  // Mensajes del chat seleccionado
  useEffect(() => {
    if (!selectedUid) { setMessages([]); return; }
    const q = query(
      collection(db, 'support_chats', selectedUid, 'messages'),
      orderBy('createdAt'),
      limitToLast(200),
    );
    return onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data({ serverTimestamps: 'estimate' }) })));
    });
  }, [selectedUid]);

  // Marcar como leidos al tener el chat en pantalla
  useEffect(() => {
    if (selectedChat?.unreadByAdmin > 0) {
      updateDoc(doc(db, 'support_chats', selectedChat.id), { unreadByAdmin: 0 }).catch(console.error);
    }
  }, [selectedChat?.id, selectedChat?.unreadByAdmin]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || sending || !selectedUid) return;
    setSending(true);
    try {
      await sendSupportMessage(selectedUid, trimmed);
      setText('');
    } catch (error) {
      console.error('Error enviando mensaje:', error);
      alert('No se pudo enviar: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  const handleClose = async () => {
    if (!selectedUid) return;
    if (!window.confirm('¿Cerrar el chat? La app del usuario volverá a funcionar con normalidad.')) return;
    setClosing(true);
    try {
      await closeSupportChat(selectedUid);
    } catch (error) {
      console.error('Error cerrando chat:', error);
      alert('No se pudo cerrar: ' + error.message);
    } finally {
      setClosing(false);
    }
  };

  const isActive = selectedChat && selectedChat.status !== 'closed';

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div>
          <h2 className="page-title">Soporte</h2>
          <p className="page-subtitle">Chats abiertos desde el panel. Mientras un chat está abierto, la app del usuario queda bloqueada.</p>
        </div>
      </div>

      <div className="support-layout">
        <div className="card support-list">
          <div className="tabs" style={{ marginBottom: '0.75rem' }}>
            <button className={`tab-btn ${tab === 'active' ? 'active-pending' : ''}`} onClick={() => setTab('active')}>Activos</button>
            <button className={`tab-btn ${tab === 'closed' ? 'active-pending' : ''}`} onClick={() => setTab('closed')}>Historial</button>
          </div>

          {loadingList ? (
            <div className="spinner" />
          ) : chats.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">💬</div>
              <p className="text-secondary text-sm">
                {tab === 'active'
                  ? 'No hay chats activos. Ábrelos desde Usuarios o Conductores.'
                  : 'Sin chats cerrados.'}
              </p>
            </div>
          ) : chats.map((chat) => (
            <button
              key={chat.id}
              className={`support-list-item ${chat.id === selectedUid ? 'selected' : ''}`}
              onClick={() => setSearchParams({ uid: chat.id })}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
                <span className="font-medium">{chat.userName || chat.id.slice(0, 8)}</span>
                {chat.unreadByAdmin > 0 && <span className="support-unread">{chat.unreadByAdmin}</span>}
              </div>
              <div className="text-xs text-secondary" style={{ marginTop: '4px' }}>
                {chat.targetType === 'driver' ? 'Conductor' : 'Pasajero'} · {formatTime(chat.lastMessageAt)}
              </div>
              <div style={{ marginTop: '6px' }}><StatusBadge chat={chat} /></div>
            </button>
          ))}
        </div>

        <div className="card support-conversation">
          {!selectedUid ? (
            <div className="empty-state" style={{ margin: 'auto' }}>
              <div className="empty-icon">💬</div>
              <p className="text-secondary">Selecciona un chat</p>
            </div>
          ) : (
            <>
              <div className="support-conversation-header">
                <div>
                  <div className="font-bold">{selectedChat?.userName || selectedUid}</div>
                  <div className="text-xs text-secondary mono-text">{selectedUid}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <StatusBadge chat={selectedChat} />
                  {isActive && (
                    <button className="btn btn-danger" onClick={handleClose} disabled={closing}>
                      {closing ? 'Cerrando…' : 'Cerrar chat'}
                    </button>
                  )}
                </div>
              </div>

              <div className="support-messages">
                {messages.length === 0 && (
                  <p className="text-secondary text-sm" style={{ textAlign: 'center', margin: 'auto' }}>Aún no hay mensajes.</p>
                )}
                {messages.map((m) => (
                  <div key={m.id} className={`support-bubble ${m.senderRole === 'admin' ? 'from-admin' : 'from-user'}`}>
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</div>
                    <div className="support-bubble-time">{formatTime(m.createdAt)}</div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              {isActive ? (
                <form className="support-input" onSubmit={handleSend}>
                  <input
                    className="input-field"
                    value={text}
                    maxLength={MAX_TEXT}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Escribe un mensaje…"
                    disabled={sending}
                  />
                  <button className="btn btn-primary" type="submit" disabled={sending || !text.trim()}>
                    {sending ? '…' : 'Enviar'}
                  </button>
                </form>
              ) : (
                <p className="text-secondary text-sm" style={{ padding: '0.75rem 1rem' }}>Chat cerrado.</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SupportChats;
